import React, { useState, useEffect } from 'react';

// ============ TYPES ============

interface WhirlpoolInstanceData {
  id?: string;
  entryId: string;
  class: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
  className: string;
  effect: string;
  effectModifiers: {
    armorMultiplier: number;
    shieldMultiplier: number;
    damageMultiplier: number;
    speedMultiplier: number;
    repairMultiplier: number;
    signatureMultiplier: number;
    description: string;
  };
  width: number;
  height: number;
  exitPoint: {
    tileX: number;
    tileY: number;
    worldX: number;
    worldY: number;
    stable: boolean;
  };
  playerCount: number;
  lootMultiplier: number;
  xpMultiplier: number;
}

interface WhirlpoolHUDProps {
  instance: WhirlpoolInstanceData | null;
  playerPosition: { x: number; y: number };
  onExit: () => void;
  expiresAt?: number;
}

// ============ CONSTANTS ============

const CLASS_COLORS: Record<string, string> = {
  C1: '#4fc3f7',
  C2: '#29b6f6',
  C3: '#7e57c2',
  C4: '#ab47bc',
  C5: '#ef5350',
  C6: '#d32f2f',
};

const EFFECT_NAMES: Record<string, string> = {
  none: 'None',
  calm_belt: 'Calm Belt',
  raging_current: 'Raging Current',
  knock_up_stream: 'Knock-Up Stream',
  leviathan_waters: 'Leviathan Waters',
  storm_surge: 'Storm Surge',
  devils_triangle: "Devil's Triangle",
};

// ============ COMPONENT ============

export const WhirlpoolHUD: React.FC<WhirlpoolHUDProps> = ({
  instance,
  playerPosition,
  onExit,
  expiresAt,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('--:--');
  const [distanceToExit, setDistanceToExit] = useState<number>(0);

  // Update time remaining
  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining('--:--');
      return;
    }

    const updateTime = (): void => {
      const now = Date.now();
      const remaining = Math.max(0, expiresAt - now);
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Update distance to exit
  useEffect(() => {
    if (!instance) return;

    const dx = playerPosition.x - instance.exitPoint.worldX;
    const dy = playerPosition.y - instance.exitPoint.worldY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    setDistanceToExit(Math.floor(dist));
  }, [instance, playerPosition]);

  if (!instance) return null;

  const classColor = CLASS_COLORS[instance.class] || '#4fc3f7';
  const effectName = EFFECT_NAMES[instance.effect] || instance.effect;

  return (
    <div
      style={{
        position: 'fixed',
        fontFamily: 'var(--font-mono)',
        top: '56px',
        right: '12px',
        width: '320px',
        background: 'rgba(13, 17, 23, 0.95)',
        border: '2px solid #30363d',
        color: '#e2e8f0',
        zIndex: 100,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: '#161b22',
          borderBottom: '1px solid #30363d',
          userSelect: 'none',
        }}
      >
        <div>
          <span
            style={{
              color: classColor,
              fontWeight: 'bold',
              fontSize: '12px',
            }}
          >
            [{instance.class}]
          </span>
        </div>
        <div
          style={{
            fontSize: '11px',
            color: timeRemaining.startsWith('0:') ? '#ef5350' : '#8b949e',
          }}
        >
          {timeRemaining}
        </div>
      </div>

      {/* Effect */}
      {instance.effect !== 'none' && (
        <div
          style={{
            margin: '12px',
            padding: '8px',
            background: '#161b22',
            border: '1px solid #21262d',
          }}
        >
          <div style={{ fontSize: '10px', color: '#f0883e', marginBottom: '4px', fontWeight: 'bold' }}>
            EFFECT
          </div>
          <div style={{ fontSize: '11px', color: '#c9d1d9', marginBottom: '6px' }}>
            {effectName}
          </div>
          <div style={{ fontSize: '10px', color: '#8b949e', lineHeight: '1.35' }}>
            {instance.effectModifiers.description}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '6px 10px',
          fontSize: '11px',
          padding: '0 12px 12px 12px',
        }}
      >
        <div>
          <span style={{ color: '#8b949e' }}>Loot</span>
          <span style={{ color: '#e2e8f0' }}>: </span>
          <span style={{ color: '#58a6ff' }}>{instance.lootMultiplier}x</span>
        </div>
        <div>
          <span style={{ color: '#8b949e' }}>XP</span>
          <span style={{ color: '#e2e8f0' }}>: </span>
          <span style={{ color: '#fbbf24' }}>{instance.xpMultiplier}x</span>
        </div>
        <div>
          <span style={{ color: '#8b949e' }}>Players</span>
          <span style={{ color: '#e2e8f0' }}>: </span>
          <span style={{ color: '#e2e8f0' }}>{instance.playerCount}</span>
        </div>
        <div>
          <span style={{ color: '#8b949e' }}>Exit</span>
          <span style={{ color: '#e2e8f0' }}>: </span>
          <span style={{ color: instance.exitPoint.stable ? '#238636' : '#f0883e' }}>
            {instance.exitPoint.stable ? 'STABLE' : 'UNSTABLE'}
          </span>
        </div>
      </div>

      {/* Exit Info */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          background: '#0d1117',
          borderTop: '1px solid #30363d',
        }}
      >
        <div style={{ fontSize: '11px' }}>
          <span style={{ color: '#58a6ff', fontWeight: 'bold' }}>EXIT POINT</span>
          <span style={{ marginLeft: '8px', color: '#8b949e' }}>{distanceToExit}px away</span>
        </div>
        {distanceToExit <= 50 && (
          <button
            onClick={onExit}
            style={{
              background: '#161b22',
              border: `1px solid ${classColor}`,
              color: '#e2e8f0',
              padding: '6px 12px',
              fontSize: '11px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            EXIT
          </button>
        )}
      </div>

      {/* Effect Modifiers (collapsed by default, show on hover) */}
      {instance.effect !== 'none' && (
        <div
          style={{
            marginTop: '8px',
            fontSize: '10px',
            color: '#666666',
          }}
        >
          <details>
            <summary style={{ cursor: 'pointer', color: '#888888' }}>
              Effect Modifiers
            </summary>
            <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
              {instance.effectModifiers.armorMultiplier !== 1.0 && (
                <div>Armor: {(instance.effectModifiers.armorMultiplier * 100).toFixed(0)}%</div>
              )}
              {instance.effectModifiers.damageMultiplier !== 1.0 && (
                <div>Damage: {(instance.effectModifiers.damageMultiplier * 100).toFixed(0)}%</div>
              )}
              {instance.effectModifiers.speedMultiplier !== 1.0 && (
                <div>Speed: {(instance.effectModifiers.speedMultiplier * 100).toFixed(0)}%</div>
              )}
              {instance.effectModifiers.repairMultiplier !== 1.0 && (
                <div>Repair: {(instance.effectModifiers.repairMultiplier * 100).toFixed(0)}%</div>
              )}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};
